import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { GpsNotificationLog } from '@/types/gps';
import { toast } from 'sonner';

export function useGpsNotifications(companyId?: string) {
  const queryClient = useQueryClient();

  // Fetch notification logs
  const { data: notifications, isLoading } = useQuery({
    queryKey: ['gps-notifications', companyId],
    queryFn: async (): Promise<GpsNotificationLog[]> => {
      if (!companyId) return [];
      
      const { data, error } = await (supabase as any)
        .from('fleet_gps.notification_logs')
        .select(`
          *,
          devices:device_id (
            name,
            imei,
            fleet_vehicles (
              plate,
              make,
              model
            )
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
    refetchInterval: 30000,
  });

  // Retry failed notification
  const retryNotification = useMutation({
    mutationFn: async (notificationId: string) => {
      const { data, error } = await supabase.functions.invoke('fleet-gps-webhook-handler', {
        body: {
          action: 'retry',
          notification_id: notificationId,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gps-notifications'] });
      toast.success('Notificação reenviada com sucesso');
    },
    onError: (error) => {
      toast.error('Erro ao reenviar notificação');
      console.error('Error retrying notification:', error);
    },
  });

  // Statistics
  const stats = {
    total: notifications?.length || 0,
    success: notifications?.filter(n => n.status === 'success').length || 0,
    failed: notifications?.filter(n => n.status === 'failed').length || 0,
    pending: notifications?.filter(n => n.status === 'pending').length || 0,
  };

  return {
    notifications,
    isLoading,
    retryNotification,
    stats,
  };
}
